import {
  Badge,
  Icon,
  Button,
  Avatar,
  AvatarFallback,
  Heading,
} from "@elaniintc/goes-design-system";
import type {
  AppealDocumentType,
  InsolvencyType,
} from "@/types/entities/insolvencies";
import {
  getInsolvencyableName,
  getInsolvencyableId,
  parseAmount,
  getPersonInitial,
  getUnitText,
  getPaymentMethodLabel,
  getHolder,
} from "./insolvency.utils";
import type { BadgeConfig } from "./insolvency.utils";
import { formatDateToCommaString } from "@/utils/dates";
import { Renderify } from "renderify-js";
import { renderSubjectAvatar } from "./insolvency-list-modal.helpers";

interface InsolvencyDetailProps {
  selected: InsolvencyType;
  statusConfig: BadgeConfig | null;
  selectedPaidOnBehalfOf: string | undefined;
  comments: React.ReactNode;
  userComments: NonNullable<InsolvencyType["comments"]>;
  isExpedientMode: boolean;
  isRejectAppealOpen: boolean;
  setIsRejectAppealOpen: (value: boolean) => void;
  rejectAppealComment: string;
  setRejectAppealComment: (value: string) => void;
  rejectAppealMutation: { isPending: boolean };
  handleRejectAppeal: () => void;
  handleRejectClick: () => void;
  handleDownload: (document: AppealDocumentType) => void;
  showActions: boolean;
}

export function InsolvencyDetail({
  selected,
  statusConfig,
  selectedPaidOnBehalfOf,
  comments,
  userComments,
  isExpedientMode,
  isRejectAppealOpen,
  setIsRejectAppealOpen,
  rejectAppealComment,
  setRejectAppealComment,
  rejectAppealMutation,
  handleRejectAppeal,
  handleRejectClick,
  handleDownload,
  showActions,
}: Readonly<InsolvencyDetailProps>) {
  return (
    <div className="flex flex-col col-span-1 py-6 px-4 overflow-y-auto">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Heading variant="h6">Información de infracción</Heading>
          <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-start justify-between gap-2 rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3">
                <div className="flex flex-col gap-1">
                  <p className="text-body font-semibold">{selected.reason}</p>
                  <p className="text-sm text-body-quiet">
                    {selected.reference}
                  </p>
                </div>
                <Badge size="sm" variant="warning" fill="ghost">
                  ${parseAmount(selected.amount).toFixed(2)}
                </Badge>
              </div>
              <div className="flex items-start justify-between gap-4 rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3">
                <div className="flex flex-col gap-1">
                  <p className="text-body font-semibold">
                    <Renderify
                      condition={
                        selected.status === "SOLVENT" ||
                        selected.status === "CANCELLED"
                      }
                      elseShow={selected.payment_deadline_value?.toString()}
                    >
                      {"0"}
                    </Renderify>{" "}
                    {getUnitText(selected.payment_deadline_unit)} para pago
                  </p>
                  <p className="text-sm text-body-quiet">
                    {selected.can_appeal
                      ? "Se puede apelar"
                      : "No se puede apelar"}
                  </p>
                </div>
                <Badge
                  size="sm"
                  variant={statusConfig?.badgeVariant}
                  fill="ghost"
                >
                  {statusConfig?.badgeLabel}
                </Badge>
              </div>
            </div>
          </section>
        </div>

        <div className="flex flex-col gap-2">
          <Heading variant="h6">Infracción asociada a</Heading>
          <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3 flex flex-col gap-3">
            <div className="flex items-center gap-3 rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3">
              {renderSubjectAvatar(selected, "md")}
              <div>
                <p className="text-body-loud font-semibold">
                  {getInsolvencyableName(selected)}
                </p>
                <p className="text-sm text-body-quiet">
                  {getInsolvencyableId(selected)}
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="flex flex-col gap-2">
          <Heading variant="h6">Comentarios</Heading>
          <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3 flex flex-col gap-3">
            <div className="rounded-lg py-1">
              <div className="text-body">{comments}</div>
            </div>
          </section>
        </div>

        {selected.appeal_documents && selected.appeal_documents.length > 0 && (
          <div className="flex flex-col gap-2">
            <Heading variant="h6">Apelación del sancionado</Heading>
            <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3 flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <p className="text-body-loud font-semibold leading-2xl text-sm">
                  Documentos de respaldo
                </p>
                <div className="flex flex-col gap-2">
                  {selected.appeal_documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between rounded-lg border border-neutral-quiet bg-neutral-object px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          type="far"
                          name="file-alt"
                          className="text-sm text-body-quiet"
                        />
                        <span className="text-body text-sm truncate max-w-30">
                          {doc.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-body-quiet text-xs border border-neutral-quiet rounded-full px-2 py-1">
                          {(doc.size / 1024).toFixed(0)} KB
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          onClick={() => handleDownload(doc)}
                          data-testid={`insolvency-download-${doc.id}`}
                          className="text-action hover:text-action-loud transition-colors"
                          aria-label={`Descargar ${doc.name}`}
                        >
                          <Icon
                            type="far"
                            name="download"
                            className="text-sm"
                          />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Renderify condition={userComments.length > 0}>
                <div className="flex flex-col gap-2">
                  <p className="text-body-loud font-semibold leading-2xl text-sm">
                    Comentario
                  </p>
                  <div className="rounded-lg py-1">
                    <div className="text-body">
                      <Renderify
                        condition={userComments.length === 1}
                        elseShow={
                          <>
                            {userComments.map((comment) => (
                              <div
                                key={comment.id}
                                className="text-sm text-body-quiet py-1"
                              >
                                - {comment.text}
                              </div>
                            ))}
                          </>
                        }
                      >
                        {userComments[0]?.text}
                      </Renderify>
                    </div>
                  </div>
                </div>
              </Renderify>
              <Renderify
                condition={
                  selected.status === "APPEAL_SUBMITTED" && showActions
                }
              >
                <div>
                  <Renderify
                    condition={isRejectAppealOpen}
                    elseShow={
                      <Renderify
                        condition={isExpedientMode}
                        elseShow={
                          <Button
                            type="button"
                            variant="danger-tertiary"
                            size="sm"
                            className="w-full"
                            onClick={() => setIsRejectAppealOpen(true)}
                            data-testid="insolvency-mark-appeal-invalid"
                          >
                            <Icon type="far" name="times-circle" />
                            Marcar apelación como inválida
                          </Button>
                        }
                      >
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="danger-tertiary"
                            size="sm"
                            className="flex-1"
                            onClick={() => setIsRejectAppealOpen(true)}
                            data-testid="insolvency-deny-appeal"
                          >
                            Denegar apelación
                          </Button>
                          <Button
                            type="button"
                            variant="tertiary"
                            size="sm"
                            className="flex-1 text-action"
                            onClick={handleRejectClick}
                            data-testid="insolvency-approve-appeal"
                          >
                            Aprobar apelación
                          </Button>
                        </div>
                      </Renderify>
                    }
                  >
                    <div className="flex flex-col gap-2">
                      <p className="text-sm font-semibold text-body-loud">
                        Motivo del rechazo
                      </p>
                      <textarea
                        className="w-full rounded-lg border border-neutral-quiet bg-neutral-object px-3 py-2 text-sm text-body resize-none focus:outline-none focus:ring-1 focus:ring-action"
                        rows={3}
                        maxLength={500}
                        placeholder="Escribe el motivo por el cual la apelación es inválida..."
                        value={rejectAppealComment}
                        onChange={(e) => setRejectAppealComment(e.target.value)}
                        data-testid="insolvency-reject-reason-textarea"
                      />
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          type="button"
                          variant="quiet"
                          size="sm"
                          onClick={() => {
                            setIsRejectAppealOpen(false);
                            setRejectAppealComment("");
                          }}
                          data-testid="insolvency-reject-cancel"
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          variant="danger-primary"
                          size="sm"
                          data-testid="insolvency-reject-confirm"
                          disabled={
                            !rejectAppealComment.trim() ||
                            rejectAppealMutation.isPending
                          }
                          onClick={handleRejectAppeal}
                        >
                          {rejectAppealMutation.isPending
                            ? "Rechazando..."
                            : "Confirmar rechazo"}
                        </Button>
                      </div>
                    </div>
                  </Renderify>
                </div>
              </Renderify>
            </section>
          </div>
        )}

        {selected.payment ? (
          <div className="flex flex-col gap-2">
            <Heading variant="h6">Información de pago</Heading>
            <section className="rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent p-3 flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <p className="text-body-loud font-semibold leading-2xl text-sm">
                  Referencia
                </p>
                <p className="text-body leading-3xl font-geist-mono text-size-body-sm">
                  {" "}
                  {selected.payment.reference}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-body-loud font-semibold leading-2xl text-sm">
                  Fecha de pago
                </p>
                <p className="text-body leading-3xl">
                  {selected?.payment?.paid_at
                    ? formatDateToCommaString(selected.payment.paid_at)
                    : "-"}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-body-loud font-semibold leading-2xl text-sm">
                  Método de pago
                </p>
                <p className="text-body leading-3xl">
                  {getPaymentMethodLabel(selected.payment.method)}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-body-loud font-semibold leading-2xl text-sm">
                  Pagador
                </p>
                <div>
                  <div className="flex items-center gap-1">
                    <Avatar size="2xs">
                      <AvatarFallback color="action" type="icon">
                        {getPersonInitial(getHolder(selected))}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-action">{getHolder(selected)}</span>
                  </div>
                  {selectedPaidOnBehalfOf && (
                    <p className="text-sm leading-2xl text-body">
                      Representando a{" "}
                      <span className="text-action">
                        {selectedPaidOnBehalfOf}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-body-loud font-semibold leading-2xl text-sm">
                  Total pagado
                </p>
                <p className="text-success-quiet font-semibold leading-3xl">
                  ${parseAmount(selected.amount).toFixed(2)}
                </p>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
